import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@sortera/react-components";
import { ArrowRightOnRectangleIcon } from "@sortera/react-icons";
import { fetchSignInTexts } from "../../integrations/sign-in-texts.ts";
import { BrandMark } from "../../ui/brand-mark/index.ts";
import photo from "./circlar-vatten.jpg";
import styles from "./sign-in.module.css";

interface Text {
  title: string;
  lead: ReactNode;
}

const TITLE = "Bygg vidare på dina projekt";

/** Ours, said a few different ways. The first always opens the page; the rest until today's arrive. */
const FIRST: Text = {
  title: TITLE,
  lead: (
    <>
      Beskriv vad du vill ha. Claude läser projektet, gör ändringarna och lägger upp dem i testmiljön <br />— utan att
      du behöver installera något.
    </>
  ),
};

const OURS: Text[] = [
  "Från idé till något du kan klicka på. Skriv vad du vill se, så bygger Claude det i ditt projekt och visar det direkt.",
  "Ändra en knapp, lägg till en sida eller bygg en hel prototyp. Du beskriver, Claude skriver koden och lägger upp den.",
  "Ingen installation och inga kommandon. Prata om ditt projekt på svenska och se det växa i förhandsvisningen bredvid.",
  "Sorteras byggrecept, designsystem och testmiljöer finns redan på plats. Du står för idén.",
  "Varje ändring sparas i ditt projekt i Azure DevOps, i ditt namn, så att inget försvinner på vägen.",
].map((lead) => ({ title: TITLE, lead }));

/** Seconds each text stays before the next fades in over it. */
const TEXT_SECONDS = 7;

/**
 * Two halves: signing in on the left, a boat drawing circles in the sea on the right. On a narrow screen
 * the photo gives way and the sign-in has the page to itself.
 */
export function SignIn({ onSignIn }: { onSignIn: () => void }) {
  const texts = useTexts();
  const { now, before } = useTurning(texts.length);
  const text = texts[now] ?? FIRST;
  const old = before === null ? null : (texts[before] ?? null);
  return (
    <main className={styles.page}>
      <section className={styles.signIn}>
        <div className={styles.content}>
          <span className={styles.brand}>
            <BrandMark size="lg" />
          </span>
          <p className="sc-kicker">Sortera Lab</p>
          {/*
            The old text fades out while the new one fades in over it, in the same place. Keyed by the
            heading itself, so a heading that stays the same stays still.
          */}
          <h1 className={`sc-heading ${styles.title} ${styles.turning}`}>
            {old && old.title !== text.title && (
              <span key={`out:${old.title}`} className={styles.fadeOut} aria-hidden>
                {old.title}
              </span>
            )}
            <span key={text.title} className={styles.fadeIn}>
              {text.title}
            </span>
          </h1>
          <p className={`${styles.lead} ${styles.turning}`} aria-live="off">
            {old && (
              <span key={`out:${before}`} className={styles.fadeOut} aria-hidden>
                {old.lead}
              </span>
            )}
            <span key={now} className={`${styles.fadeIn} ${styles.leadText}`}>
              {text.lead}
            </span>
          </p>
          <Button onClick={onSignIn} icon={<ArrowRightOnRectangleIcon />}>
            Logga in med ditt Sortera-konto
          </Button>
          <p className={styles.note}>Du behöver tillhöra rätt grupp i Entra — fråga din kontaktperson om du inte kommer in.</p>
        </div>
      </section>

      <aside className={styles.showcase} aria-hidden>
        <img className={styles.photo} src={photo} alt="" draggable={false} />
      </aside>
    </main>
  );
}

/** Ours to begin with; once today's have come from the server, the first of ours and then theirs. */
function useTexts(): Text[] {
  const [texts, setTexts] = useState<Text[]>([FIRST, ...OURS]);
  useEffect(() => {
    let current = true;
    void fetchSignInTexts().then((today) => {
      if (current && today.length > 0) setTexts([FIRST, ...today]);
    });
    return () => {
      current = false;
    };
  }, []);
  return texts;
}

/** The text to show now and the one before it, turning to the next every few seconds while the page is open. */
function useTurning(count: number): { now: number; before: number | null } {
  const [turn, setTurn] = useState({ now: 0, before: null as number | null });
  useEffect(() => {
    // Text that changes by itself is motion too; for those who opted out, the first one stays.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const timer = setInterval(
      () => setTurn(({ now }) => ({ now: (now + 1) % count, before: now % count })),
      TEXT_SECONDS * 1000,
    );
    return () => clearInterval(timer);
  }, [count]);
  return { now: turn.now % count, before: turn.before };
}
